<div #highlight class="te-pick-highlight"></div>

<aside *ngIf="open" class="te-panel" [class.te-dock-left]="dockLeft" aria-label="Theme editor">
  <header class="te-panel-header">
    <div>
      <h2>Theme colors</h2>
      <span class="te-theme-name">Editing: {{ theme }}</span>
    </div>
    <div class="te-header-actions">
      <button type="button" class="te-icon-btn" (click)="dockLeft = !dockLeft" title="Move panel to the other side">&#8644;</button>
      <button type="button" class="te-icon-btn" (click)="closed.emit()" title="Close" aria-label="Close">&times;</button>
    </div>
  </header>

  <button type="button" class="te-pick-btn" [class.te-active]="picking" (click)="togglePicking()">
    {{ picking ? "Click an element on the page (Esc to cancel)" : "Pick an element" }}
  </button>
  <p class="te-hint">Alt + Shift + C also starts picking, and keeps dropdowns and menus open.</p>

  <nav class="te-tabs">
    <button type="button" [class.te-active]="tab === 'element'" (click)="setTab('element')">Element</button>
    <button type="button" [class.te-active]="tab === 'component'" (click)="setTab('component')">Component</button>
    <button type="button" [class.te-active]="tab === 'all'" (click)="setTab('all')">All colors</button>
    <button type="button" [class.te-active]="tab === 'changes'" (click)="setTab('changes')">
      Changes<span *ngIf="changedCount" class="te-count">{{ changedCount }}</span>
    </button>
  </nav>

  <div class="te-panel-body">
    <ng-container *ngIf="tab === 'element'">
      <p *ngIf="!pickedLabel" class="te-empty">Pick an element to see which colors control it.</p>
      <ng-container *ngIf="pickedLabel">
        <p class="te-picked">{{ pickedLabel }}</p>
        <ng-container *ngIf="elementRows.length"><ng-container *ngTemplateOutlet="scopeSelect"></ng-container></ng-container>
        <ng-container *ngFor="let row of elementRows">
          <ng-container *ngTemplateOutlet="colorRow; context: { $implicit: row }"></ng-container>
        </ng-container>
        <p *ngIf="!elementRows.length && !fixedColors.length" class="te-empty">No colors found on this element.</p>
        <ng-container *ngTemplateOutlet="fixedList; context: { $implicit: fixedColors }"></ng-container>
        <button type="button" class="te-link-btn" (click)="flagForDeveloper()">Flag this element for a developer</button>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="tab === 'component'">
      <p *ngIf="!pickedLabel" class="te-empty">Pick an element to see every color in its component and everything inside it.</p>
      <ng-container *ngIf="pickedLabel">
        <div class="te-scope">
          <label for="te-root-select">Show colors in</label>
          <select id="te-root-select" (change)="setComponentRoot($any($event.target).value)">
            <option *ngFor="let root of componentRoots; let i = index" [value]="i" [selected]="i === componentRootIndex">{{ root.label }}</option>
          </select>
          <p class="te-hint">{{ componentRows.length }} editable color{{ componentRows.length === 1 ? "" : "s" }} across {{ componentElementCount }} element{{ componentElementCount === 1 ? "" : "s" }}. The picked element also lists its hover, focus and other states, like selected or expanded, even while they're off.</p>
        </div>
        <ng-container *ngIf="componentRows.length"><ng-container *ngTemplateOutlet="scopeSelect"></ng-container></ng-container>
        <ng-container *ngFor="let row of componentRows">
          <ng-container *ngTemplateOutlet="colorRow; context: { $implicit: row }"></ng-container>
        </ng-container>
        <p *ngIf="!componentRows.length && !componentFixed.length" class="te-empty">No colors found here.</p>
        <ng-container *ngTemplateOutlet="fixedList; context: { $implicit: componentFixed }"></ng-container>
        <button type="button" class="te-link-btn" (click)="flagForDeveloper()">Flag this element for a developer</button>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="tab === 'all'">
      <input
        class="te-search"
        type="search"
        placeholder="Search colors"
        aria-label="Search colors"
        [value]="search"
        (input)="search = $any($event.target).value"
      />
      <p *ngIf="!allRows.length" class="te-empty">This page doesn't use theme colors. Open a themed page and try again.</p>
      <ng-container *ngFor="let row of filteredAllRows">
        <ng-container *ngTemplateOutlet="colorRow; context: { $implicit: row }"></ng-container>
      </ng-container>
    </ng-container>

    <ng-container *ngIf="tab === 'changes'">
      <p *ngIf="!changedCount" class="te-empty">Nothing changed yet.</p>
      <ng-container *ngFor="let row of changeRows">
        <ng-container *ngTemplateOutlet="colorRow; context: { $implicit: row }"></ng-container>
      </ng-container>
      <div *ngIf="flags.length" class="te-flags">
        <h3>Flagged for a developer</h3>
        <div *ngFor="let flag of flags" class="te-flag">
          <span>{{ flag }}</span>
          <button type="button" class="te-icon-btn" (click)="removeFlag(flag)" title="Remove" aria-label="Remove">&times;</button>
        </div>
      </div>
    </ng-container>
  </div>

  <footer *ngIf="changedCount" class="te-panel-footer">
    <button type="button" class="te-primary" (click)="copyExport()">{{ copied ? "Copied" : "Copy changes" }}</button>
    <button type="button" (click)="downloadExport()">Download</button>
    <button type="button" class="te-danger" (click)="resetAll()">Reset all</button>
  </footer>
</aside>

<ng-template #colorRow let-row>
  <div class="te-row" [class.te-changed]="isChanged(row)">
    <div class="te-row-top">
      <code class="te-token" [title]="row.token">{{ row.token }}</code>
      <button
        *ngIf="isChanged(row)"
        type="button"
        class="te-link-btn"
        (click)="resetRow(row)"
        [title]="'Back to ' + wasValue(row)"
      >
        Reset
      </button>
    </div>
    <div class="te-row-edit">
      <input
        *ngIf="row.hex"
        type="color"
        [attr.aria-label]="row.token + ' color'"
        [value]="row.hex"
        (input)="onColorInput(row, $any($event.target).value)"
      />
      <input
        class="te-value"
        type="text"
        spellcheck="false"
        [class.te-invalid]="row.invalid"
        [attr.aria-label]="row.token + ' value'"
        [value]="row.value"
        (change)="onTextInput(row, $any($event.target).value)"
      />
    </div>
    <label *ngIf="row.hex" class="te-alpha">
      <span>Opacity</span>
      <input type="range" min="0" max="100" [value]="percent(row.alpha)" (input)="onAlphaInput(row, $any($event.target).value)" />
      <span class="te-alpha-value">{{ percent(row.alpha) }}%</span>
    </label>
    <p *ngIf="row.invalid" class="te-error">Not a valid color.</p>
    <p *ngIf="row.uses" class="te-uses">{{ row.uses }}</p>
  </div>
</ng-template>

<ng-template #scopeSelect>
  <div class="te-scope">
    <label for="te-scope-select">Apply changes to</label>
    <select id="te-scope-select" [value]="scope" (change)="setScope($any($event.target).value)">
      <option value="">Everywhere in the theme</option>
      <option *ngFor="let option of scopeOptions" [value]="option.selector" [selected]="option.selector === scope">{{ option.label }}</option>
    </select>
    <p class="te-hint">{{ scope ? "Only this part changes. The rest of the site keeps its colors." : "Changes this color everywhere it's used." }}</p>
  </div>
</ng-template>

<ng-template #fixedList let-colors>
  <div *ngIf="colors.length" class="te-fixed">
    <h3>Fixed in the code</h3>
    <p class="te-hint">These can't be edited here. Flag the element so a developer can make them editable.</p>
    <div *ngFor="let color of colors" class="te-fixed-color">
      <span>{{ color.where }}{{ color.count ? " · " + color.count + (color.count === 1 ? " element" : " elements") : "" }}</span>
      <code>{{ color.value }}</code>
    </div>
  </div>
</ng-template>
